/**
 * Real-time Currency & Live Exchange Rate Service
 * Handles live conversion between international currencies with automatic sync and caching
 */

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  flag: string;
  decimals: number;
}

export const SUPPORTED_CURRENCIES: Record<string, CurrencyInfo> = {
  INR: { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳', decimals: 2 },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸', decimals: 2 },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺', decimals: 2 },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧', decimals: 2 },
  AED: { code: 'AED', symbol: 'AED', name: 'UAE Dirham', flag: '🇦🇪', decimals: 2 },
  CAD: { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦', decimals: 2 },
  AUD: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺', decimals: 2 },
  SGD: { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', flag: '🇸🇬', decimals: 2 },
  SAR: { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal', flag: '🇸🇦', decimals: 2 },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵', decimals: 0 },
  MYR: { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', flag: '🇲🇾', decimals: 2 },
  BRL: { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', flag: '🇧🇷', decimals: 2 }
};

// Map symbol or code to standard 3-letter currency code
export function normalizeCurrencyCode(symbolOrCode: string): string {
  if (!symbolOrCode) return 'INR';
  const clean = symbolOrCode.trim();
  
  if (SUPPORTED_CURRENCIES[clean.toUpperCase()]) {
    return clean.toUpperCase();
  }

  for (const [code, info] of Object.entries(SUPPORTED_CURRENCIES)) {
    if (info.symbol === clean || info.code.toLowerCase() === clean.toLowerCase()) {
      return code;
    }
  }

  // Fallbacks for specific common symbols
  if (clean === '₹') return 'INR';
  if (clean === '$') return 'USD';
  if (clean === '€') return 'EUR';
  if (clean === '£') return 'GBP';
  if (clean === '¥') return 'JPY';
  if (clean === 'C$') return 'CAD';
  if (clean === 'A$') return 'AUD';
  if (clean === 'S$') return 'SGD';
  if (clean === 'RM') return 'MYR';

  return 'INR';
}

export function getCurrencySymbol(codeOrSymbol: string): string {
  const code = normalizeCurrencyCode(codeOrSymbol);
  return SUPPORTED_CURRENCIES[code]?.symbol || codeOrSymbol || '₹';
}

// Built-in baseline exchange rates (Base: USD)
export const DEFAULT_EXCHANGE_RATES: Record<string, number> = {
  USD: 1.0,
  INR: 95.43,
  EUR: 0.92,
  GBP: 0.79,
  AED: 3.67,
  CAD: 1.39,
  AUD: 1.54,
  SGD: 1.34,
  SAR: 3.75,
  JPY: 153.20,
  MYR: 4.45,
  BRL: 5.75
};

const RATES_STORAGE_NAME = 'vdp_live_currency_rates_v1';
const RATES_TIMESTAMP_NAME = 'vdp_live_currency_timestamp_v1';
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

export interface LiveRateState {
  rates: Record<string, number>;
  lastUpdated: Date;
  isLive: boolean;
  base: string;
}

let cachedState: LiveRateState = {
  rates: { ...DEFAULT_EXCHANGE_RATES },
  lastUpdated: new Date(),
  isLive: false,
  base: 'USD'
};

// Initialize cache from localStorage if available
try {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(RATES_STORAGE_NAME);
    const savedTime = localStorage.getItem(RATES_TIMESTAMP_NAME);
    if (saved && savedTime) {
      const parsed = JSON.parse(saved);
      const time = new Date(parseInt(savedTime, 10));
      cachedState = {
        rates: { ...DEFAULT_EXCHANGE_RATES, ...parsed },
        lastUpdated: time,
        isLive: true,
        base: 'USD'
      };
    }
  }
} catch {
  // Ignore localStorage errors
}

// Subscribers for real-time rates changes
type RateListener = (state: LiveRateState) => void;
const listeners = new Set<RateListener>();

export function subscribeToRates(listener: RateListener): () => void {
  listeners.add(listener);
  listener(cachedState);
  return () => {
    listeners.delete(listener);
  };
}

function notifyListeners() {
  listeners.forEach(l => l(cachedState));
}

/**
 * Fetch live exchange rates from public exchange API with fallback
 */
export async function fetchLiveExchangeRates(forceRefresh = false): Promise<LiveRateState> {
  const now = Date.now();
  const cachedTime = cachedState.lastUpdated.getTime();
  
  if (!forceRefresh && cachedState.isLive && (now - cachedTime < CACHE_DURATION_MS)) {
    return cachedState;
  }

  try {
    // Try primary free API
    const res = await fetch('https://open.er-api.com/v6/latest/USD', {
      headers: { 'Accept': 'application/json' }
    });

    if (res.ok) {
      const data = await res.json();
      if (data && data.rates) {
        const newRates: Record<string, number> = { ...DEFAULT_EXCHANGE_RATES };
        for (const code of Object.keys(SUPPORTED_CURRENCIES)) {
          if (typeof data.rates[code] === 'number') {
            newRates[code] = data.rates[code];
          }
        }

        cachedState = {
          rates: newRates,
          lastUpdated: new Date(),
          isLive: true,
          base: 'USD'
        };

        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(RATES_STORAGE_NAME, JSON.stringify(newRates));
            localStorage.setItem(RATES_TIMESTAMP_NAME, String(Date.now()));
          } catch {}
        }

        notifyListeners();
        return cachedState;
      }
    }
  } catch (err) {
    // Silently fall back if primary API fails (e.g. CORS or adblockers)
  }

  try {
    // Secondary fallback
    const res2 = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
    if (res2.ok) {
      const data2 = await res2.json();
      if (data2 && data2.rates) {
        const newRates: Record<string, number> = { ...DEFAULT_EXCHANGE_RATES };
        for (const code of Object.keys(SUPPORTED_CURRENCIES)) {
          if (typeof data2.rates[code] === 'number') {
            newRates[code] = data2.rates[code];
          }
        }

        cachedState = {
          rates: newRates,
          lastUpdated: new Date(),
          isLive: true,
          base: 'USD'
        };

        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(RATES_STORAGE_NAME, JSON.stringify(newRates));
            localStorage.setItem(RATES_TIMESTAMP_NAME, String(Date.now()));
          } catch {}
        }

        notifyListeners();
        return cachedState;
      }
    }
  } catch (err2) {
    // Silently fall back to cached default rates if secondary API also fails
  }

  // If network calls fail, keep using existing cached state but update timestamp to avoid spamming failed requests
  cachedState.lastUpdated = new Date();
  
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(RATES_TIMESTAMP_NAME, String(Date.now()));
    } catch {}
  }
  
  return cachedState;
}

// Auto fetch on module load
if (typeof window !== 'undefined') {
  setTimeout(() => {
    fetchLiveExchangeRates();
  }, 1000);
}

/**
 * Convert value between currencies using live or cached rates
 */
export function convertCurrency(
  amount: number,
  fromCodeOrSymbol: string,
  toCodeOrSymbol: string,
  customRates?: Record<string, number>
): number {
  if (!amount || isNaN(amount) || amount === 0) return 0;
  
  const fromCode = normalizeCurrencyCode(fromCodeOrSymbol);
  const toCode = normalizeCurrencyCode(toCodeOrSymbol);

  if (fromCode === toCode) return amount;

  const rates = customRates || cachedState.rates || DEFAULT_EXCHANGE_RATES;
  const rateFrom = rates[fromCode] || 1;
  const rateTo = rates[toCode] || 1;

  // Convert from currency -> USD -> to currency
  const inUSD = amount / rateFrom;
  const inTarget = inUSD * rateTo;

  // Smart rounding: if target value is small (< 1), keep 3-4 decimals, else 2 decimals
  if (inTarget < 1) {
    return Math.round(inTarget * 1000) / 1000;
  }
  return Math.round(inTarget * 100) / 100;
}

/**
 * Get human readable exchange rate string e.g. "1 USD = 86.80 INR" or "1 EUR = ₹94.35"
 */
export function getExchangeRatePairString(
  fromCodeOrSymbol: string,
  toCodeOrSymbol: string
): string {
  const fromCode = normalizeCurrencyCode(fromCodeOrSymbol);
  const toCode = normalizeCurrencyCode(toCodeOrSymbol);
  const fromInfo = SUPPORTED_CURRENCIES[fromCode];
  const toInfo = SUPPORTED_CURRENCIES[toCode];

  const rate = convertCurrency(1, fromCode, toCode);
  return `1 ${fromInfo?.code || fromCode} = ${toInfo?.symbol || ''}${rate.toFixed(toInfo?.decimals || 2)} ${toInfo?.code || toCode}`;
}

/**
 * Format currency value with symbol, commas, and proper decimals
 */
export function formatLiveCurrency(
  amount: number,
  codeOrSymbol: string,
  includeCode = false
): string {
  if (amount === undefined || amount === null || isNaN(amount)) return '0.00';
  const code = normalizeCurrencyCode(codeOrSymbol);
  const info = SUPPORTED_CURRENCIES[code] || SUPPORTED_CURRENCIES.INR;

  const decimals = info.decimals !== undefined ? info.decimals : 2;
  const formatted = amount.toLocaleString(code === 'INR' ? 'en-IN' : 'en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  return includeCode ? `${info.symbol}${formatted} ${info.code}` : `${info.symbol}${formatted}`;
}

export function getCurrentRatesState(): LiveRateState {
  return cachedState;
}
